import { useCallback } from 'react';
import { List, ListListsResponse } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { UseInfiniteQueryResult } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, SearchField, useModalControl } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { CreateListView } from '../types';
import { DeleteListModal } from './delete-list-modal';
import { ListsContent } from './lists-content';
import { ListsViewFooter } from './lists-view-footer';
import { ListsViewHeader } from './lists-view-header';
import { useDebouncedSearch } from './use-debounced-search';

type Props = {
  lists: List[];
  listsQueryResult: UseInfiniteQueryResult<ListListsResponse, unknown>;
  isListSelected: (list: List) => boolean;
  toggleList: (list: List) => void;
  startDeleteList: (list: List) => void;
  cancelDeleteList: () => void;
  finishDeleteList: () => Promise<void>;
  startEditList: (list: List) => Promise<void>;
  startRenameList: (list: List) => void;
  cancelRenameList: () => void;
  finishRenameList: (updatedName: string) => Promise<void>;
  startCreateListFrom: (fromView: CreateListView) => void;
  cancelCreateList: () => void;
  finishCreateList: (listName: string) => Promise<void>;
};

export const ListsView = ({
  lists,
  listsQueryResult,
  isListSelected,
  toggleList,
  startDeleteList,
  cancelDeleteList,
  finishDeleteList,
  startEditList,
  startRenameList,
  cancelRenameList,
  finishRenameList,
  startCreateListFrom,
  cancelCreateList,
  finishCreateList,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { listsSearchQuery, listToUpdate, mode, setListsSearchQuery, trackingIdPrefix } = useAudienceTray();

  const isDisabled =
    mode === 'lists-create-from-lists' ||
    mode === 'lists-create-from-recipients' ||
    mode === 'lists-create-from-upload' ||
    mode === 'lists-rename-list';

  const { searchFieldProps, isDebouncing } = useDebouncedSearch(
    listsSearchQuery,
    setListsSearchQuery,
    listsQueryResult.isFetched
  );

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const modifiedStartDeleteList = useCallback(
    (list: List) => {
      deleteTriggerProps.onClick();
      startDeleteList(list);
    },
    [deleteTriggerProps, startDeleteList]
  );

  const modifiedCancelDeleteList = useCallback(() => {
    deleteModalProps.onClose();
    cancelDeleteList();
  }, [deleteModalProps, cancelDeleteList]);

  const modifiedFinishDeleteList = useCallback(async () => {
    await finishDeleteList();
    deleteModalProps.onClose();
  }, [deleteModalProps, finishDeleteList]);

  // TODO: It is possible that a selected contact list has been deleted from the backend, but its contacts still make up a segment that exists on the campaign.
  // However, because the saved contact list no longer exists, it will not show up as selected in the contact lists tab.
  // So, display all segments that do not have a match with the current list of contact lists as segments so the user can remove and interact with them from the segments tab.
  return (
    <>
      <div
        css={{
          alignItems: 'center',
          columnGap: theme.spacing(1),
          display: 'grid',
          gridTemplateColumns: '1fr auto auto', // TODO: Adjust this to hide the create list button when in multi-location mode
          margin: theme.spacing(2, 4),
        }}
      >
        <SearchField
          {...searchFieldProps}
          aria-label={t('Search Lists')}
          data-trackingid={`${trackingIdPrefix}-contacts-list-search-input`}
          maxLength={70}
          name='search-lists'
          placeholder={t('Search Lists')}
          disabled={isDisabled}
        />
        <Button
          aria-label={t('Create List')}
          css={{ padding: theme.spacing(1) }}
          hoverLabel={t('Create List')}
          onClick={() => startCreateListFrom('lists')}
          trackingId={`${trackingIdPrefix}-contacts-create-list-csv-btn`}
          variant='tertiary'
          iconName='plus'
          size='large'
          disabled={isDisabled}
        />
        <Button
          aria-label={t('Upload CSV List')}
          css={{ padding: theme.spacing(1) }}
          hoverLabel={t('Upload CSV List')}
          onClick={() => startCreateListFrom('upload')}
          trackingId={`${trackingIdPrefix}-contacts-upload-csv-btn`}
          variant='tertiary'
          iconName='upload'
          size='large'
          disabled={isDisabled}
        />
      </div>

      <ListsContent
        lists={lists}
        listsQueryResult={listsQueryResult}
        startDeleteList={modifiedStartDeleteList}
        startEditList={startEditList}
        cancelCreateList={cancelCreateList}
        finishCreateList={finishCreateList}
        startRenameList={startRenameList}
        cancelRenameList={cancelRenameList}
        finishRenameList={finishRenameList}
        isListSelected={isListSelected}
        toggleList={toggleList}
        isDebouncing={isDebouncing}
      />

      <DeleteListModal
        cancelDeleteList={modifiedCancelDeleteList}
        finishDeleteList={modifiedFinishDeleteList}
        listName={listToUpdate?.name}
        {...deleteModalProps}
      />
    </>
  );
};

ListsView.Footer = ListsViewFooter;
ListsView.Header = ListsViewHeader;
